import { h, Component } from 'preact';
import cx from 'classnames';
import debounce from '@os/debounce';
import shallowEqual from '@os/shallow-equal';
import style from './style';

const ellipsis = String.fromCharCode(0x2026);

/**
 * Truncate text to a given character number
 * @param {String} text The text to truncate
 * @param {Integer} characters The maximum number of characters in the truncated string
 */
const truncateText = (text, characters) => text.length > characters ? text.slice(0, characters - 1).trim() + ellipsis : text;

const nonWordRegex = /[\W\s]/;

/**
 * Strip all non-word characters off the end of a string
 * @param {String} text The text to strip of non-word trailing characters.
 */
const stripNonWord = (text) => {
	let lastChar = text[text.length - 1];
	if (nonWordRegex.test(lastChar)) {
		return stripNonWord(text.substring(0, text.length - 1));
	}
	return text.trim();
};

let updateStyleQueue = [];

const _flushUpdateStyleQueue = debounce(() => {
	updateStyleQueue.forEach(inst => inst && !inst.isUnmounted && inst.updateStyles());
	updateStyleQueue = [];
});

function queueUpdateStyle(inst) {
	if (updateStyleQueue.indexOf(inst) === -1) updateStyleQueue.push(inst);
	_flushUpdateStyleQueue();
}

/**
 * Truncate text to the closest word to a given number of characters
 * @param {String} text The text to truncate
 * @param {Integer} characters The maximum number of characters in the truncated string
 */
export const truncateToClosestWord = (text, characters) => {
	if (text.length > characters) {
		// Subtract one character to account for the ellipsis
		let nextChar, lastSpace,
			truncated = text.substr(0, characters - 1),
			stripped = stripNonWord(truncated);

		// Return if non-word characters are removed
		if (stripped.length < truncated.length) {
			return stripped + ellipsis;
		}

		// Return if the next character is a non word
		nextChar = text[truncated.length];
		if (nextChar !== ' ' && nonWordRegex.test(nextChar)) {
			return stripped + ellipsis;
		}

		// Return the last full word
		lastSpace = text.substr(0, characters).lastIndexOf(' ');
		return stripNonWord(text.substr(0, lastSpace)) + ellipsis;
	}
	return text;
};

// inspired by https://stackoverflow.com/questions/4259815/how-do-i-detect-the-inherited-background-color-of-an-element-using-jquery-js
export function getBackgroundColor(el) {
	let bgColor = window.getComputedStyle(el).getPropertyValue('background-color');

	if ((bgColor !== 'rgba(0, 0, 0, 0)') && (bgColor !== 'transparent')) return bgColor;

	// crawl up dom and find first ancestor with background color
	let parentNode = el.parentNode;
	return parentNode && parentNode !== document.body ?  getBackgroundColor(parentNode) : '';
}

export class Truncate extends Component {

	truncateCharacterLimit(characters, text, closestWord, className, dataAttrs) {
		return (
			<span class={className} {...dataAttrs}>
				{
					closestWord ? truncateToClosestWord(text, characters) : truncateText(text, characters)
				}
			</span>
		);
	}

	truncateSingleLine(text, className, dataAttrs) {
		return (
			<span class={cx(style.truncateSingleLine, className)} {...dataAttrs}>
				{text}
			</span>
		);
	}

	truncateMultipleLinesWithFade(lines, text, className, dataAttrs) {
		return (
			<div class={className}>
				<div class={style.truncateMultipleLines}>
					<span class={style.text} style={this.state.textStyle} {...dataAttrs}>{text}</span>
					<span class={cx(style.overlay, this.props.overlayStyle)} style={this.state.overlayStyle} />
				</div>
			</div>
		);
	}

	truncateMultipleLinesWithEllipsis(lines, text, className, textStyle, ellipsesWordIndex, dataAttrs) {
		let children;

		if (ellipsesWordIndex === false) {
			//Just use the text as-is if we've determined that it can fit without overflowing
			children = text;
		}
		else {
			let wordSpans = text.split(' ');
			if (ellipsesWordIndex) {
				//remove all words starting at ellipsesWordIndex and inject ellipsis onto the end, making a text node
				wordSpans.splice(ellipsesWordIndex, wordSpans.length);
				wordSpans[ellipsesWordIndex-1] += ellipsis;
				children = wordSpans.join(' ');
			}
			else {
				//render each word wrapped in a span so we can figure out the first one that overflows
				children = wordSpans.map( (w, i) => <span>{i ? ' ' + w : w }</span>);
			}
		}

		return (
			<div class={cx(style.truncateMultipleLines, className)}>
				<span class={style.text} style={textStyle} {...dataAttrs}>{children}</span>
			</div>
		);
	}

	addEllipses(textContainer, lineHeight, textHeight, maxHeight) {
		// actual DOM nodes that were rendered, one for each word
		let wordSpans = textContainer.children || [];
		let containerBottom = textContainer.offsetTop + maxHeight;
		let lineHeightTop = (lineHeight-textHeight)/2;

		// start from the last word and iterate down until you find one that is not overflowed
		let i =  wordSpans.length;
		while (--i > 0 && wordSpans[i].offsetTop+textHeight+lineHeightTop > containerBottom);

		if (i === wordSpans.length-1)  return { ellipsesWordIndex: false };

		//Take off one more word than possibly necessary to ensure the ellipses fit
		return { ellipsesWordIndex: i };
	}

	getCriticalStyles = () => {
		// Calculate max height based on line-height and max number of lines
		let baseStyles = window.getComputedStyle(this.base);

		//properties needed to tell the difference: line-height, width, padding, font-family, font-weight
		return {
			width: baseStyles.getPropertyValue('width'),
			lineHeight: parseFloat(baseStyles.getPropertyValue('line-height')),
			paddingLeft: baseStyles.getPropertyValue('padding-left'),
			paddingRight: baseStyles.getPropertyValue('padding-right'),
			fontFamily: baseStyles.getPropertyValue('font-family'),
			fontSize: parseFloat(baseStyles.getPropertyValue('font-size')),
			fontWeight: baseStyles.getPropertyValue('font-weight')
		};

	}

	isMultiLineTruncation = ({ lines=1, method }) => this.base && method === 'multipleLines' && lines > 1

	updateStyles = () => {
		let { lines=1, fadeText } = this.props;

		// if it's not multiline truncation, there are no styles to update;
		if (!this.isMultiLineTruncation(this.props)) return;

		//properties needed to tell the difference: line-height, width, padding, font-family, font-weight
		let criticalStyles = this.getCriticalStyles();

		let lineHeight = criticalStyles.lineHeight;
		let maxHeight = lineHeight * lines;

		// Set text container style based on calc'd max height
		let textStyle = `line-height: ${lineHeight}px; max-height: ${maxHeight}px;`;

		// when re-rendering due to some signal from parent, see if anything has changed that would require us to actually re-render
		if (this.verifyNeedToReRender) {
			this.verifyNeedToReRender = false;
			if (shallowEqual(criticalStyles, this.criticalStyles)) return;

			//we need to re-render with all of the spans because something changed
			//note: because setState is async, this queueUpdateStyle won't be called until after the current _flushUpdateStyleQueue finishes
			return this.setState({ ellipsesWordIndex: undefined, textStyle, overlayStyle: undefined }, () => queueUpdateStyle(this));
		}

		this.criticalStyles = criticalStyles;

		let newState = {};

		if (textStyle !== this.state.textStyle) {
			newState.textStyle = textStyle;
		}

		if (fadeText) {
			// overlay's height should be slightly more than the line-height
			// top pushes the overlay down so it's always located at the maximum line
			// Because its container is hidden, will not display if the max-lines isn't reached
			newState.overlayStyle = `height: ${lineHeight * 1.1}px; top: ${lineHeight * (this.props.lines-1)}px;`;

			let bgColor = getBackgroundColor(this.base);
			if (bgColor) {
				newState.overlayStyle += ` background: linear-gradient(to right, rgba(255, 255, 255, 0), ${bgColor} 50%);`;
			}
		}
		else {
			// if it's multiline and no fadeText prop is provided, truncate with ellipsis
			let text = this.base.querySelector(`.${style.text}`);
			Object.assign(newState, this.addEllipses(text, lineHeight, criticalStyles.fontSize, maxHeight));
		}

		if (Object.keys(newState).length) this.setState(newState);
	}

	componentDidMount() {
		setTimeout(() => {
			// wait for the browser to render the component before calculating styles
			this.updateStyles();
		},300);
	}

	componentWillReceiveProps(nextProps) {
		//Everthing in this method is for multi-line truncation, so can be ignored if we aren't doing that
		if (!this.isMultiLineTruncation(nextProps)) return;

		let nextText = (nextProps.children[0] || '').trim();
		let currentText = (this.props.children[0] || '').trim();

		// We should verify if we need to re-render (by checking critical computed styles) if everything else that would cause us to verify is the same as last render
		this.verifyNeedToReRender =
			nextProps.lines === this.props.lines &&
			nextText === currentText &&
			nextProps.class === this.props.class &&
			nextProps.fadeText === this.props.fadeText;

		//If we know we need to re-render, ellipsesWordIndex to undefined so all word <span>s get rendered again
		if (!this.verifyNeedToReRender) {
			this.setState({ ellipsesWordIndex: undefined });
		}
		queueUpdateStyle(this);
	}

	shouldComponentUpdate() {
		return !this.verifyNeedToReRender;
	}

	componentWillUnmount() {
		this.isUnmounted=true;
	}

	render ({ children, method, characters, closestWord, fadeText, lines, overlayStyle, ...props }, { textStyle, ellipsesWordIndex }) {
		let text = (children[0] || '').trim();
		const dataAttrs = {
			...(props['data-tb-title'] && { 'data-tb-title': props['data-tb-title'] }),
			...(props['data-tb-description'] && { 'data-tb-description': props['data-tb-description'] })
		};

		if (method === 'characterLimit') {
			return this.truncateCharacterLimit(characters, text, closestWord, props.class, dataAttrs);
		}

		if ( method === 'multipleLines' && lines > 1) {
			return fadeText ?
				this.truncateMultipleLinesWithFade(lines, text, props.class, dataAttrs) :
				this.truncateMultipleLinesWithEllipsis(lines, text, props.class, textStyle, ellipsesWordIndex, dataAttrs);
		}

		return this.truncateSingleLine(text, props.class, dataAttrs);
	}
}

export default Truncate;
